import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import CloudinaryImage from "@/components/cloudinary-image";
import ProfileCard from "@/components/profile-card";
import { Button } from "@/components/ui/button";
import { formatDateDisplay } from "@/helpers/parsers";
import { cn } from "@/lib/utils";

import type { OperatorResponseType } from "../types";
import { ArrowDownIcon } from "./review-content/icons";

interface OperatorResponseProps {
  isFullReview: boolean;
  response: OperatorResponseType;
}

export default function OperatorResponse({
  isFullReview,
  response: { body, createdAt, user },
}: OperatorResponseProps) {
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const [showMoreButton, setShowMoreButton] = useState(false);

  useEffect(() => {
    if (isFullReview || !bodyRef.current) {
      return;
    }

    const { clientHeight, scrollHeight } = bodyRef.current;
    setShowMoreButton(scrollHeight > clientHeight);
  }, [isFullReview]);

  return (
    <div className="mt-5 flex flex-col items-stretch gap-[15px] border-white/20 border-l-4 py-1 pl-5 md:mt-6 md:pl-7.5">
      <div className="relative flex items-center gap-2.5">
        <CloudinaryImage
          className="size-6 rounded-full"
          rW={80}
          size={24}
          user={user}
        />
        <h5 className="font-semibold text-15/[1.2em] text-white">
          <ProfileCard userId={user.id}>
            <Link
              className="link-overlay hover:underline"
              to={`/@${user.username}`}
            >
              {user.username}
            </Link>
          </ProfileCard>
          <small className="font-normal text-cool-gray md:text-heather">
            {" "}
            (Owner)
          </small>
        </h5>
      </div>
      <div>
        <p
          className={cn(
            "whitespace-pre-line text-14/[1.7em] text-white/80 md:text-white",
            !isFullReview && "line-clamp-3"
          )}
          ref={bodyRef}
        >
          {body}
        </p>
        {showMoreButton && (
          <Button className="mt-0.5 flex h-6 items-center gap-2 font-semibold text-14 text-white">
            Show more <ArrowDownIcon />
          </Button>
        )}
      </div>
      <p className="text-14/[1.2em] text-cool-gray md:text-heather">
        {formatDateDisplay(createdAt)}
      </p>
    </div>
  );
}
